/* eslint-disable no-unused-vars */
'use client';

import { donateLink, donateLinkEn } from '@/constants';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import Button from '../ui/Button';
import Link from '../ui/Link';
import Section from './Section';
import VolunteerForm from '../forms/VolunteerForm';

enum Mode {
    Donate = 'donate',
    Volunteer = 'volunteer',
    Sponsor = 'sponsor',
    Employment = 'employment',
}

const SECTION_ID = 'get-involved';

export default function GetInvolvedSection() {
    const t = useTranslations();
    const locale = useLocale();
    const [mode, setMode] = useState<Mode>(Mode.Donate);
    const router = useRouter();

    return (
        <Section id={SECTION_ID} className="bg-bg-100 relative max-2xl:min-h-0 lg:py-0">
            <div className="px-5 lg:px-10 flex justify-between max-lg:flex-col lg:mt-44 max-lg:gap-12 lg:gap-10 w-full mb-10">
                <div className="w-full lg:w-3/12 top-44 lg:sticky lg:h-full lg:pb-32">
                    <div className="max-sm:flex-wrap flex lg:flex-col">
                        <Button
                            className="[&>div]:flex"
                            textClasses="text-nowrap max-lg:text-[30px] max-sm:text-h6 max-lg:text-h5 lg:text-h4 2xl:text-h3"
                            variant="secondary"
                            title={t('Buttons.donate')}
                            index={1}
                            isActive={mode === Mode.Donate}
                            onClick={() => {
                                setMode(Mode.Donate);
                                router.push(`#${SECTION_ID}`);
                            }}
                        />
                        <Button
                            className="[&>div]:flex"
                            textClasses="text-nowrap max-lg:text-[30px] max-sm:text-h6 max-lg:text-h5 lg:text-h4 2xl:text-h3"
                            variant="secondary"
                            title={t('Buttons.volunteer')}
                            index={2}
                            isActive={mode === Mode.Volunteer}
                            onClick={() => {
                                setMode(Mode.Volunteer);
                                router.push(`#${SECTION_ID}`);
                                // const href =
                                //     locale === 'fr'
                                //         ? 'https://docs.google.com/forms/d/1J8dghOAjJxbmIUaQgC-hIpjx8kkSEzkTmXfIVypFSWA'
                                //         : 'https://docs.google.com/forms/d/1PZDOtmeNa8wJXCWSDB_sfwOl5FUAR3Jlyc3gRr9N3E8';
                                // window.open(href, '_blank');
                            }}
                        />
                        <Button
                            className="[&>div]:flex"
                            textClasses="text-nowrap max-lg:text-[30px] max-sm:text-h6 max-lg:text-h5 lg:text-h4 2xl:text-h3"
                            variant="secondary"
                            title={t('Buttons.sponsor')}
                            index={3}
                            isActive={mode === Mode.Sponsor}
                            onClick={() => {
                                setMode(Mode.Sponsor);
                                router.push(`#${SECTION_ID}`);
                            }}
                        />
                        <Button
                            className="[&>div]:flex"
                            textClasses="text-nowrap max-lg:text-[30px] max-sm:text-h6 max-lg:text-h5 lg:text-h4 2xl:text-h3"
                            variant="secondary"
                            title={t('Buttons.employment')}
                            index={4}
                            isActive={mode === Mode.Employment}
                            onClick={() => {
                                setMode(Mode.Employment);
                                router.push(`#${SECTION_ID}`);
                            }}
                        />
                    </div>
                </div>

                <div className="w-full lg:w-7/12 2xl:min-h-[80vh]">
                    {mode === 'donate' && <Donate />}
                    {mode === 'volunteer' && <VolunteerForm />}
                    {mode === 'sponsor' && <Sponsor />}
                    {mode === 'employment' && <Employment />}
                </div>
            </div>
        </Section>
    );
}

const Donate = () => {
    const t = useTranslations();
    const locale = useLocale();

    return (
        <>
            <div className="flex flex-col gap-7 pb-7 font-helveticaNeue font-light text-sm lg:text-p">
                <p>
                    {t.rich('GetInvolved.donate.ourMandate', {
                        strong: (chunks) => <strong className="font-bold">{chunks}</strong>,
                    })}
                </p>
                <div>
                    <span>{t('GetInvolved.donate.yourSupportWill')}</span>
                    <ul className="list-decimal pl-4">
                        <li>{t('GetInvolved.donate.1')}</li>
                        <li>{t('GetInvolved.donate.2')}</li>
                        <li>
                            {t.rich('GetInvolved.donate.3', {
                                strong: (chunks) => <strong className="font-bold">{chunks}</strong>,
                            })}
                        </li>
                        <li>{t('GetInvolved.donate.4')}</li>
                        <li>{t('GetInvolved.donate.5')}</li>
                        <li>{t('GetInvolved.donate.6')}</li>
                        <li>{t('GetInvolved.donate.7')}</li>
                    </ul>
                </div>
                {/* <span>{t('GetInvolved.donate.allDonationsWill')}</span> */}
            </div>
            <div className="flex flex-col gap-7 lg:gap-[60px] w-full">
                <div className="flex items-center gap-7 max-sm:flex-col flex-wrap w-full">
                    <Link
                        className="max-sm:w-full"
                        href={locale === 'en' ? donateLinkEn : donateLink}
                        target="_blank"
                    >
                        <Button
                            className="max-sm:w-full p-4 pb-3"
                            variant="secondary"
                            title={t('Buttons.donateNow')}
                        />
                    </Link>
                    {/* <Link
                        className="border border-primary p-4 pb-3 max-sm:w-full"
                        href="/"
                        target="_blank"
                    >
                        <Button
                            className="max-sm:w-full"
                            textClasses="max-lg:text-wrap"
                            variant="ghost"
                            title={t('Buttons.canadianCompaniesOffering')}
                        />
                    </Link> */}
                </div>
            </div>
        </>
    );
};

const Sponsor = () => {
    const t = useTranslations();

    return (
        <div className="flex flex-col gap-7 pb-7 font-helveticaNeue font-light text-sm lg:text-p">
            <span className="font-tradeGothic font-bold text-[48px] lg:text-h3 uppercase">
                {t('GetInvolved.sponsor.incqis')}
            </span>
            <ul className="list-disc pl-4">
                <li>{t('GetInvolved.sponsor.1')}</li>
                <li>{t('GetInvolved.sponsor.2')}</li>
                <li>{t('GetInvolved.sponsor.3')}</li>
            </ul>
            <p>
                {t.rich('GetInvolved.sponsor.aPartnership', {
                    br: (chunks) => <br />,
                })}
            </p>
            <ul className="list-disc pl-4">
                <li>{t('GetInvolved.sponsor.4')}</li>
                <li>{t('GetInvolved.sponsor.5')}</li>
                <li>{t('GetInvolved.sponsor.6')}</li>
                <li>{t('GetInvolved.sponsor.7')}</li>
                <li>{t('GetInvolved.sponsor.8')}</li>
                <li>{t('GetInvolved.sponsor.9')}</li>
            </ul>
            <span>
                {t.rich('GetInvolved.sponsor.pleaseContact', {
                    link: (chunks) => (
                        <Link
                            className="text-primary"
                            href="mailto:charlie@image-nation.org"
                            title={`${chunks}`}
                            target="_blank"
                        />
                    ),
                })}
            </span>
        </div>
    );
};

const Employment = () => {
    const t = useTranslations();

    return (
        <div className="flex flex-col gap-7 pb-7 font-helveticaNeue font-light text-sm lg:text-p">
            <span className="font-tradeGothic font-bold text-[48px] lg:text-h3 uppercase">
                {t('GetInvolved.employment.joinTheTeam')}
            </span>
            <p>{t('GetInvolved.employment.inqc')}</p>
            <p>{t('GetInvolved.employment.everEvolving')}</p>
            <p>{t('GetInvolved.employment.doYouWant')}</p>
            <div>
                <span className="font-tradeGothic font-bold text-h4 uppercase">
                    {t('GetInvolved.employment.jobOpportunities')}
                </span>
                <div className="border border-blue-100 p-5 flex flex-col gap-2">
                    <span className="text-blue-100 font-tradeGothic font-bold uppercase leading-none text-md lg:text-h6">
                        {t.rich('GetInvolved.employment.noAvailable', {
                            br: (chunks) => <br />,
                        })}
                    </span>
                    <span>
                        {t.rich('GetInvolved.employment.ifYouWant', {
                            link: (chunks) => (
                                <Link
                                    className="text-primary"
                                    href="mailto:charlie@image-nation.org"
                                    title={`${chunks}`}
                                    target="_blank"
                                />
                            ),
                        })}
                    </span>
                </div>
            </div>
            <p>{t('GetInvolved.employment.atin')}</p>
        </div>
    );
};
